import * as pdfjsLib from 'pdfjs-dist';
import type {
  PDFDocumentProxy,
  PDFPageProxy,
  TextContent,
  RenderTask,
} from 'pdfjs-dist/types/src/display/api';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

export interface RenderOptions {
  scale: number;
  rotation: number;
  canvas: HTMLCanvasElement;
}

export interface PageDimensions {
  width: number;
  height: number;
  originalWidth: number;
  originalHeight: number;
}

export class PDFEngine {
  private document: PDFDocumentProxy | null = null;
  private pageCache: Map<number, PDFPageProxy> = new Map();
  private activeRenderTasks: Map<HTMLCanvasElement, RenderTask> = new Map();

  async loadDocument(
    source: ArrayBuffer | Uint8Array | string,
    onProgress?: (progress: number) => void
  ): Promise<PDFDocumentProxy> {
    this.destroy();

    const dataCopy =
      source instanceof ArrayBuffer
        ? new Uint8Array(source.slice(0))
        : source instanceof Uint8Array
        ? source.slice(0)
        : source;

    const loadingTask = pdfjsLib.getDocument({
      data: dataCopy,
      cMapUrl: '/cmaps/',
      cMapPacked: true,
      standardFontDataUrl: '/standard_fonts/',
      enableXfa: true,
    });

    if (onProgress) {
      loadingTask.onProgress = (data: { loaded: number; total: number }) => {
        if (data.total > 0) {
          onProgress(data.loaded / data.total);
        }
      };
    }

    this.document = await loadingTask.promise;
    return this.document;
  }

  async getPage(pageNumber: number): Promise<PDFPageProxy> {
    if (!this.document) throw new Error('No document loaded');
    if (pageNumber < 1 || pageNumber > this.document.numPages) {
      throw new Error(`Invalid page number: ${pageNumber}`);
    }

    if (this.pageCache.has(pageNumber)) {
      return this.pageCache.get(pageNumber)!;
    }

    const page = await this.document.getPage(pageNumber);
    this.pageCache.set(pageNumber, page);
    return page;
  }

  async renderPage(pageNumber: number, options: RenderOptions): Promise<PageDimensions> {
    const page = await this.getPage(pageNumber);
    const BASE_SCALE = 1.3333;
    const viewport = page.getViewport({ 
      scale: options.scale * BASE_SCALE, 
      rotation: (page.rotate + (options.rotation || 0)) % 360 
    });

    const canvas = options.canvas;
    const context = canvas.getContext('2d')!;

    // Cancel existing render task on this canvas if any
    if (this.activeRenderTasks.has(canvas)) {
      try {
        this.activeRenderTasks.get(canvas)!.cancel();
      } catch {
        // Ignore cancellation error
      }
      this.activeRenderTasks.delete(canvas);
    }

    // Support HiDPI
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(viewport.width * dpr);
    canvas.height = Math.floor(viewport.height * dpr);
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;

    // Reset transform before rendering
    context.setTransform(dpr, 0, 0, dpr, 0, 0);

    const renderContext: any = {
      canvasContext: context,
      viewport,
    };

    const renderTask = page.render(renderContext);
    this.activeRenderTasks.set(canvas, renderTask);

    try {
      await renderTask.promise;
    } catch (err: any) {
      if (err?.name !== 'RenderingCancelledException') {
        throw err;
      }
    } finally {
      if (this.activeRenderTasks.get(canvas) === renderTask) {
        this.activeRenderTasks.delete(canvas);
      }
    }

    return {
      width: viewport.width,
      height: viewport.height,
      originalWidth: page.getViewport({ scale: 1.3333 }).width,
      originalHeight: page.getViewport({ scale: 1.3333 }).height,
    };
  }

  async getTextContent(pageNumber: number): Promise<TextContent> {
    const page = await this.getPage(pageNumber);
    return page.getTextContent();
  }

  async extractFullText(): Promise<string> {
    if (!this.document) throw new Error('No document loaded');

    const texts: string[] = [];
    for (let i = 1; i <= this.document.numPages; i++) {
      const textContent = await this.getTextContent(i);
      const pageText = textContent.items
        .filter((item: any) => 'str' in item)
        .map((item: any) => item.str)
        .join(' ');
      texts.push(pageText);
    }
    return texts.join('\n\n');
  }

  async getPageDimensions(pageNumber: number, scale: number = 1): Promise<PageDimensions> {
    const page = await this.getPage(pageNumber);
    const BASE_SCALE = 1.3333;
    const viewport = page.getViewport({ scale: scale * BASE_SCALE });
    return {
      width: viewport.width,
      height: viewport.height,
      originalWidth: page.getViewport({ scale: BASE_SCALE }).width,
      originalHeight: page.getViewport({ scale: BASE_SCALE }).height,
    };
  }

  getPageCount(): number {
    return this.document?.numPages || 0;
  }

  async getMetadata() {
    if (!this.document) return null;
    const metadata = await this.document.getMetadata();
    return metadata;
  }

  async getOutline() {
    if (!this.document) return null;
    return this.document.getOutline();
  }

  destroy() {
    this.activeRenderTasks.forEach((task) => {
      try {
        task.cancel();
      } catch {
        // ignore
      }
    });
    this.activeRenderTasks.clear();
    this.pageCache.clear();
    if (this.document) {
      try {
        const doc = this.document as any;
        if (typeof doc.destroy === 'function') {
          doc.destroy();
        } else if (typeof doc.cleanup === 'function') {
          doc.cleanup();
        }
      } catch {
        // ignore
      }
      this.document = null;
    }
  }
}

// Singleton instance for the main viewer
export const pdfEngine = new PDFEngine();
